import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { theme } from '../../../theme';

interface HourSlot {
  hour: number;
  occupancyRate: number; // 0-100
}

interface DayOccupancy {
  dayName: string;
  slots: HourSlot[];
}

interface OccupancyHeatmapProps {
  data: DayOccupancy[];
  weekLabel: string;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  venueId?: string;
}

const HOURS = [8, 10, 12, 14, 16, 18, 20, 22];
const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const getOccupancyColor = (rate: number): string => {
  if (rate === -1) return '#33383B'; // Sin disponibilidad
  if (rate >= 80) return theme.colors.success; // Verde (80-100%)
  if (rate >= 40) return theme.colors.warning; // Amarillo (40-79%)
  return theme.colors.danger; // Rojo (0-39%)
};

const getOccupancyLabel = (rate: number): string => {
  if (rate === -1) return 'Sin disponibilidad';
  return `${rate}% ocupado`;
};

const OccupancyHeatmap: React.FC<OccupancyHeatmapProps> = ({
  data,
  weekLabel,
  onPreviousWeek,
  onNextWeek,
  venueId,
}) => {
  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: '18px',
    border: `1px solid ${theme.colors.border}`,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '24px',
    color: theme.colors.text,
    letterSpacing: '1px',
    margin: 0,
  };

  const weekNavStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  };

  const navBtnStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  };

  const weekLabelStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    fontWeight: '600',
    color: theme.colors.text,
  };

  const dayLabelsStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '40px repeat(7, 1fr)',
    gap: '8px',
    paddingRight: '40px',
  };

  const dayLabelStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    fontWeight: '600',
    color: theme.colors.textSecondary,
    textAlign: 'center',
  };

  const gridContainerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '40px repeat(7, 1fr)',
    gap: '8px',
  };

  const hourLabelsStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const hourLabelStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '10px',
    color: theme.colors.textSecondary,
    height: '28px',
    display: 'flex',
    alignItems: 'center',
  };

  const dayColumnStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const getCellStyle = (occupancyRate: number): React.CSSProperties => ({
    width: '100%',
    height: '28px',
    borderRadius: '4px',
    backgroundColor: getOccupancyColor(occupancyRate),
    cursor: 'pointer',
    transition: 'transform 0.2s, opacity 0.2s',
  });

  const legendStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    paddingTop: '12px',
  };

  const legendItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const legendBoxStyle = (color: string): React.CSSProperties => ({
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    backgroundColor: color,
  });

  const legendTextStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '11px',
    color: theme.colors.textSecondary,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>OCUPACIÓN SEMANAL</h3>
        <div style={weekNavStyle}>
          <button style={navBtnStyle} onClick={onPreviousWeek}>
            <ChevronLeft size={16} color={theme.colors.textSecondary} />
          </button>
          <span style={weekLabelStyle}>{weekLabel}</span>
          <button style={navBtnStyle} onClick={onNextWeek}>
            <ChevronRight size={16} color={theme.colors.textSecondary} />
          </button>
        </div>
      </div>

      {/* Day Labels */}
      <div style={dayLabelsStyle}>
        <div /> {/* Empty cell for hour labels column */}
        {DAY_LABELS.map((day, index) => (
          <div key={index} style={dayLabelStyle}>
            {day}
          </div>
        ))}
      </div>

      {/* Heatmap Grid */}
      <div style={gridContainerStyle}>
        {/* Hour Labels */}
        <div style={hourLabelsStyle}>
          {HOURS.map((hour) => (
            <div key={hour} style={hourLabelStyle}>
              {hour.toString().padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* Day Columns */}
        {data.map((dayData, dayIndex) => (
          <div key={dayIndex} style={dayColumnStyle}>
            {dayData.slots.map((slot, slotIndex) => {
              const occupancyRate = slot.occupancyRate;
              return (
                <div
                  key={slotIndex}
                  style={getCellStyle(occupancyRate)}
                  title={`${dayData.dayName} ${slot.hour}:00 - ${getOccupancyLabel(occupancyRate)}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.opacity = '1';
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={legendStyle}>
        <div style={legendItemStyle}>
          <div style={legendBoxStyle(theme.colors.success)} />
          <span style={legendTextStyle}>80-100% ocupado</span>
        </div>
        <div style={legendItemStyle}>
          <div style={legendBoxStyle(theme.colors.warning)} />
          <span style={legendTextStyle}>40-79% ocupado</span>
        </div>
        <div style={legendItemStyle}>
          <div style={legendBoxStyle(theme.colors.danger)} />
          <span style={legendTextStyle}>0-39% ocupado</span>
        </div>
        <div style={legendItemStyle}>
          <div style={legendBoxStyle('#33383B')} />
          <span style={legendTextStyle}>Sin disponibilidad</span>
        </div>
      </div>
    </div>
  );
};

export default OccupancyHeatmap;
